import type { UnitsPreference } from './unitsPreference';

/**
 * Cook mode (ROADMAP §3 WS-19) — ephemeral, per-device, per-recipe "in progress" state:
 * which ingredient lines are checked off tonight, which step you're on, and any running
 * timers. Deliberately **not** Yjs. Checking off "2 cups flour" while cooking must not
 * touch the shared `recipes` doc — Faye reading the same recipe on her phone must see it
 * exactly as written, unaffected by whatever's happening in the kitchen right now. This is
 * the one place in the app that intentionally does NOT sync, as a contrast with every other
 * module (lists/calendar/recipes content itself, all Yjs-backed).
 *
 * Persisted to `localStorage` only so an accidental tab close / reload doesn't lose
 * progress mid-cook; "Start over" in `CookModePage` calls `clearCookSession` to reset.
 * Keyed by recipe id, so cooking two different recipes never collides.
 */

export interface CookSessionState {
  checkedIngredientIds: string[];
  currentStepIndex: number;
  scaleFactor: number;
  unitsPreference: UnitsPreference;
  /** Serialized `CookTimersState['timers']` — plain JSON, no functions/Dates. */
  timers: Array<{
    id: string;
    name: string;
    durationSeconds: number;
    status: 'running' | 'paused' | 'done';
    endTime: number | null;
    remainingAtPause: number | null;
  }>;
}

export type CookSessionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function storageKey(recipeId: string): string {
  return `awwyeah:cook:${recipeId}`;
}

function isValidStatus(value: unknown): value is 'running' | 'paused' | 'done' {
  return value === 'running' || value === 'paused' || value === 'done';
}

/** Loose shape-check on parsed JSON — a corrupt/older-shape value is treated as "no session"
 * rather than thrown, since this is best-effort convenience state, never a source of truth. */
function isCookSessionState(value: unknown): value is CookSessionState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v.checkedIngredientIds) &&
    typeof v.currentStepIndex === 'number' &&
    typeof v.scaleFactor === 'number' &&
    Array.isArray(v.timers)
  );
}

export function readCookSession(
  storage: CookSessionStorage,
  recipeId: string,
): CookSessionState | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(storageKey(recipeId));
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isCookSessionState(parsed)) return null;
    return {
      checkedIngredientIds: parsed.checkedIngredientIds.filter(
        (x): x is string => typeof x === 'string',
      ),
      currentStepIndex: parsed.currentStepIndex,
      scaleFactor: parsed.scaleFactor,
      unitsPreference: parsed.unitsPreference,
      timers: parsed.timers.filter(
        (t): t is CookSessionState['timers'][number] =>
          !!t &&
          typeof t === 'object' &&
          typeof (t as { id?: unknown }).id === 'string' &&
          isValidStatus((t as { status?: unknown }).status),
      ),
    };
  } catch {
    return null;
  }
}

export function writeCookSession(
  storage: CookSessionStorage,
  recipeId: string,
  state: CookSessionState,
): void {
  try {
    storage.setItem(storageKey(recipeId), JSON.stringify(state));
  } catch {
    // Best-effort — a failed write just means the next reload starts fresh.
  }
}

export function clearCookSession(storage: CookSessionStorage, recipeId: string): void {
  try {
    storage.removeItem(storageKey(recipeId));
  } catch {
    // Nothing to do — worst case the stale entry lingers until overwritten.
  }
}
